import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'

import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined'

import { NOTIFICATION_TYPE_ICONS, DEFAULT_NOTIFICATION_ICON } from './notificationTypeIcons'

dayjs.extend(relativeTime)

// Item de notificación compartido entre el popover (NotificationBell) y la
// página completa (/notifications) — así el tratamiento visual leída/no
// leída (punto, franja lateral, opacidad) se mantiene igual en los dos sitios
// sin duplicar el JSX. `onDelete` es opcional: si no se pasa, no se muestra
// el botón de borrar (el popover no lo necesita, solo la página).
const NotificationListItem = ({ notification, onClick, onDelete }) => {
    const { t } = useTranslation()
    const Icon = NOTIFICATION_TYPE_ICONS[notification.type] || DEFAULT_NOTIFICATION_ICON
    const isUnread = !notification.readAt

    return (
        <ListItemButton
            onClick={onClick}
            sx={{
                alignItems: 'flex-start',
                borderLeft: '3px solid',
                borderLeftColor: isUnread ? 'primary.main' : 'transparent',
                bgcolor: isUnread ? 'action.hover' : 'transparent',
                '&:hover': {
                    bgcolor: isUnread ? 'action.selected' : 'action.hover'
                }
            }}>
            <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                <Icon fontSize='small' color={isUnread ? 'primary' : 'disabled'} />
            </ListItemIcon>
            <ListItemText
                primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        {isUnread && (
                            <Box
                                sx={{
                                    width: 7,
                                    height: 7,
                                    flexShrink: 0,
                                    borderRadius: '50%',
                                    bgcolor: 'primary.main'
                                }}
                            />
                        )}
                        <Typography
                            variant='body2'
                            fontWeight={isUnread ? 600 : 400}
                            color={isUnread ? 'text.primary' : 'text.secondary'}>
                            {notification.title}
                        </Typography>
                    </Box>
                }
                secondary={
                    <>
                        {notification.body && (
                            <Typography
                                variant='caption'
                                color='text.secondary'
                                component='span'
                                sx={{ display: 'block', opacity: isUnread ? 1 : 0.7 }}>
                                {notification.body}
                            </Typography>
                        )}
                        <Typography
                            variant='caption'
                            color='text.disabled'
                            component='span'
                            sx={{ display: 'block' }}>
                            {dayjs(notification.createdAt).fromNow()}
                        </Typography>
                    </>
                }
            />
            {onDelete && (
                <Tooltip title={t('Delete')}>
                    <IconButton
                        size='small'
                        edge='end'
                        onClick={(e) => {
                            e.stopPropagation()
                            onDelete(notification.tid)
                        }}
                        sx={{ mt: 0.5 }}>
                        <DeleteOutlineIcon fontSize='small' />
                    </IconButton>
                </Tooltip>
            )}
        </ListItemButton>
    )
}

export default NotificationListItem
